import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = window.location.origin.includes('localhost') ? 'http://localhost:8083' : '';

// Mock Data
const NOTIFICATION_HISTORY = [
  { id: 1, title: "Test Results: Physics Mock 2", channel: "App & Push", sentTo: "Batch 2025", time: "2 hours ago", status: "Delivered (98%)" },
  { id: 2, title: "Parent Update: Monthly Performance", channel: "WhatsApp", sentTo: "Parents (All)", time: "Yesterday", status: "Read (85%)" },
  { id: 3, title: "Schedule Change: Math Lecture", channel: "SMS", sentTo: "JEE 2024", time: "2 days ago", status: "Delivered (100%)" },
];

export default function Notifications() {
  const [filterStatus, setFilterStatus] = useState('punched_in');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [smsMessage, setSmsMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [broadcastChannel, setBroadcastChannel] = useState('sms'); // 'sms' or 'email'
  const [emailSubject, setEmailSubject] = useState('');
  const [showSmtpModal, setShowSmtpModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState({
    host: '', port: 587, username: '', password: '', fromEmail: '', auth: true, startTls: true
  });
  const [whatsappSettings, setWhatsappSettings] = useState({
    apiKey: '', 
    phoneNumberId: '', 
    businessAccountId: '',
    whatsappAutoNotifyEnabled: false,
    whatsappAutoNotifyStartTime: '08:00',
    whatsappAutoNotifyEndTime: '20:00',
    whatsappTimeInTemplate: '',
    whatsappTimeOutTemplate: ''
  });
  const [smsSettings, setSmsSettings] = useState({
    apiKey: '', senderId: '', provider: 'msg91', accountSid: ''
  });
  const [notifiedStudents, setNotifiedStudents] = useState([]);
  const [notifType, setNotifType] = useState('TIME_IN');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('22:00');
  const [hideSent, setHideSent] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);

  const fetchFilteredStudents = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      
      let filterUrl = `${API_BASE_URL}/api/students/attendance/filter?status=${filterStatus}&date=${filterDate}`;
      if (filterStatus === 'punched_in' || filterStatus === 'punched_out') {
        filterUrl += `&startTime=${startTime}&endTime=${endTime}`;
      }

      const [res, notifiedRes] = await Promise.all([
        axios.get(filterUrl, { headers }),
        axios.get(`${API_BASE_URL}/api/students/attendance/notified?date=${filterDate}`, { headers })
      ]);

      setFilteredStudents(res.data);
      setNotifiedStudents(notifiedRes.data);
      setSelectedEmails(res.data.map(s => s.email));
      setHasSearched(true);
      
      // Fetch WhatsApp Templates if channel is whatsapp
      fetchTemplates();
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch students based on attendance.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      const res = await axios.get(`${API_BASE_URL}/api/students/whatsapp/templates`, { headers });
      setWaTemplates(res.data);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  React.useEffect(() => {
    if (broadcastChannel === 'whatsapp') {
      fetchTemplates();
    }
  }, [broadcastChannel]);

  const handleSendSms = async () => {
    if (selectedEmails.length === 0 || !smsMessage.trim()) {
      toast.warning("Please select students and enter a message.");
      return;
    }

    setSending(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      
      const payload = {
        studentEmails: selectedEmails,
        message: smsMessage,
        date: filterDate,
        type: notifType
      };

      await axios.post(`${API_BASE_URL}/api/students/send-sms`, payload, { headers });
      toast.success(`SMS Broadcast successfully sent to ${selectedEmails.length} recipients.`);
      setSmsMessage('');
      setFilteredStudents([]);
      setSelectedEmails([]);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to send SMS broadcast.");
    } finally {
      setSending(false);
    }
  };

  const handleSendEmail = async () => {
    if (selectedEmails.length === 0 || !emailSubject.trim() || !smsMessage.trim()) {
      toast.warning("Please select students, enter a subject, and enter a message.");
      return;
    }

    setSending(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      
      const payload = {
        studentEmails: selectedEmails,
        subject: emailSubject,
        message: smsMessage,
        date: filterDate,
        type: notifType
      };

      await axios.post(`${API_BASE_URL}/api/students/send-email`, payload, { headers });
      toast.success(`Email Broadcast successfully sent to ${selectedEmails.length} recipients.`);
      setSmsMessage('');
      setEmailSubject('');
      setFilteredStudents([]);
      setSelectedEmails([]);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to send Email broadcast. Check SMTP settings.");
    } finally {
      setSending(false);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      const res = await axios.get(`${API_BASE_URL}/api/tenant/settings`, { headers });
      if (res.data) {
        setSmtpSettings({
          host: res.data.smtpHost || '',
          port: res.data.smtpPort || 587,
          username: res.data.smtpUsername || '',
          password: res.data.smtpPassword || '',
          fromEmail: res.data.smtpFromEmail || '',
          auth: res.data.smtpAuth !== false,
          startTls: res.data.smtpStartTls !== false
        });
        setWhatsappSettings({
          apiKey: res.data.whatsappApiKey || '',
          phoneNumberId: res.data.whatsappPhoneNumberId || '',
          businessAccountId: res.data.whatsappBusinessAccountId || '',
          whatsappAutoNotifyEnabled: res.data.whatsappAutoNotifyEnabled || false,
          whatsappAutoNotifyStartTime: res.data.whatsappAutoNotifyStartTime || '08:00',
          whatsappAutoNotifyEndTime: res.data.whatsappAutoNotifyEndTime || '20:00',
          whatsappTimeInTemplate: res.data.whatsappTimeInTemplate || '',
          whatsappTimeOutTemplate: res.data.whatsappTimeOutTemplate || ''
        });
        setSmsSettings({
          apiKey: res.data.smsApiKey || '',
          senderId: res.data.smsSenderId || '',
          provider: res.data.smsProvider || 'msg91',
          accountSid: res.data.smsAccountSid || ''
        });
      }
    } catch (err) {
      console.error("Failed to fetch settings", err);
    }
  };

  const handleSaveSmtp = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      await axios.post(`${API_BASE_URL}/api/tenant/settings/smtp`, smtpSettings, { headers });
      toast.success("SMTP Settings updated successfully!");
      setShowSmtpModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save SMTP settings.");
    }
  };

  const handleSaveWhatsAppSettings = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };

      const payload = {
        whatsappApiKey: whatsappSettings.apiKey,
        whatsappPhoneNumberId: whatsappSettings.phoneNumberId,
        whatsappBusinessAccountId: whatsappSettings.businessAccountId,
        whatsappAutoNotifyEnabled: whatsappSettings.whatsappAutoNotifyEnabled,
        whatsappAutoNotifyStartTime: whatsappSettings.whatsappAutoNotifyStartTime,
        whatsappAutoNotifyEndTime: whatsappSettings.whatsappAutoNotifyEndTime,
        whatsappTimeInTemplate: whatsappSettings.whatsappTimeInTemplate,
        whatsappTimeOutTemplate: whatsappSettings.whatsappTimeOutTemplate
      };
      await axios.post(`${API_BASE_URL}/api/theme`, payload, { headers });
      toast.success("WhatsApp Settings updated successfully!");
      setShowWhatsAppModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save WhatsApp settings.");
    }
  };

  const handleSaveSmsSettings = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      await axios.post(`${API_BASE_URL}/api/tenant/settings/sms`, smsSettings, { headers });
      toast.success("SMS Settings updated successfully!");
      setShowSmsModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save SMS settings.");
    }
  };

  const handleSendWhatsapp = async () => {
    if (selectedEmails.length === 0 || (!smsMessage.trim() && !selectedTemplate)) {
      toast.warning("Please select students and enter a message or select a template.");
      return;
    }

    setSending(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT';
      const headers = { 'X-Tenant-ID': tenantId };
      
      // Assemble parameters: use keywords for auto-fill fields, and user input for manual fields
      const resolvedParameters = selectedTemplate?.parameters?.split(',').map((p, idx) => {
        const keyword = p.trim().toLowerCase();
        const autoFields = ['name', 'email', 'date', 'batch', 'time_in', 'time_out'];
        if (autoFields.includes(keyword)) {
          return keyword; // Send keyword for backend to resolve
        }
        return templateParams[idx] || ''; // Send manual value
      }) || [];

      const payload = {
        studentEmails: selectedEmails,
        message: smsMessage,
        date: filterDate,
        type: notifType,
        templateName: selectedTemplate?.templateName,
        languageCode: selectedTemplate?.languageCode,
        parameters: resolvedParameters
      };

      console.log("Initiating WhatsApp Broadcast:", payload);

      await axios.post(`${API_BASE_URL}/api/students/send-whatsapp`, payload, { headers });
      toast.success(`WhatsApp Broadcast successfully sent to ${selectedEmails.length} recipients.`);
      setSmsMessage('');
      setFilteredStudents([]);
      setSelectedEmails([]);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to send WhatsApp broadcast. Check API settings.");
    } finally {
      setSending(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedEmails.length === filteredStudents.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(filteredStudents.map(s => s.email));
    }
  };

  const toggleStudent = (email) => {
    setSelectedEmails(prev => 
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
            Multi-Channel Communications
          </h1>
          <p className="mt-2 text-lg text-slate-400 max-w-2xl">
            Broadcast test reminders, performance alerts, and parent updates instantly via App, WhatsApp, and SMS.
          </p>
        </div>
        
        <button className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-medium transition-all shadow-lg shadow-pink-500/20 flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"></path><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
          Compose Message
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Communication Engine */}
        <div className="lg:col-span-2 space-y-8">
          
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 relative overflow-hidden">
            <h2 className="text-xl font-semibold text-slate-200 mb-6">Automated Trigger Workflows</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              <div className="p-5 rounded-2xl bg-slate-800/40 border border-slate-700/50 hover:border-pink-500/30 transition-colors group cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
                </div>
                <h3 className="font-semibold text-slate-200 mb-1">Test Reminders</h3>
                <p className="text-xs text-slate-400 mb-4">Automatically send App & SMS reminders 2 hours before scheduled exams.</p>
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Active</span>
                  <div className="w-10 h-5 bg-pink-500 rounded-full relative cursor-pointer"><div className="w-4 h-4 bg-white rounded-full absolute right-0.5 top-0.5 shadow"></div></div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-slate-800/40 border border-slate-700/50 hover:border-green-500/30 transition-colors group cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <h3 className="font-semibold text-slate-200 mb-1">Parent Updates (WhatsApp)</h3>
                <p className="text-xs text-slate-400 mb-4">Send weekly attendance and test score PDF summaries to registered parent numbers.</p>
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Active</span>
                  <div className="w-10 h-5 bg-green-500 rounded-full relative cursor-pointer"><div className="w-4 h-4 bg-white rounded-full absolute right-0.5 top-0.5 shadow"></div></div>
                </div>
              </div>

            </div>
          </section>

          {/* Smart SMS Broadcast Section */}
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-200 flex items-center gap-3">
                <span className="p-2 rounded-lg bg-orange-500/10 text-orange-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                </span>
                Smart {broadcastChannel === 'sms' ? 'SMS' : broadcastChannel === 'email' ? 'Email' : 'WhatsApp'} Broadcast
              </h2>
              <div className="flex items-center gap-4">
                <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-700/50">
                  <button 
                    onClick={() => setBroadcastChannel('sms')}
                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase transition-all ${broadcastChannel === 'sms' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                  >
                    SMS
                  </button>
                  <button 
                    onClick={() => setBroadcastChannel('email')}
                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase transition-all ${broadcastChannel === 'email' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                  >
                    Email
                  </button>
                  <button 
                    onClick={() => setBroadcastChannel('whatsapp')}
                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase transition-all ${broadcastChannel === 'whatsapp' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                  >
                    WA
                  </button>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => { fetchSmtpSettings(); setShowSmtpModal(true); }}
                    className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700/50 transition-colors"
                    title="Configure SMTP"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </button>
                  <button 
                    onClick={() => { fetchSmtpSettings(); setShowWhatsAppModal(true); }}
                    className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700/50 transition-colors"
                    title="WhatsApp Settings"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  </button>
                  <button 
                    onClick={() => { fetchSmtpSettings(); setShowSmsModal(true); }}
                    className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700/50 transition-colors"
                    title="SMS Settings"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 bg-slate-800/20 p-5 rounded-2xl border border-slate-800/60">
               <div className="space-y-2">
                 <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Filter By Status</label>
                 <select 
                   value={filterStatus}
                   onChange={(e) => {
                     const status = e.target.value;
                     setFilterStatus(status);
                     if (status === 'punched_in') setNotifType('TIME_IN');
                     else if (status === 'punched_out') setNotifType('TIME_OUT');
                   }}
                   className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-slate-200 focus:border-orange-500 outline-none transition-all cursor-pointer font-medium"
                 >
                   <option value="punched_in">Punched In</option>
                   <option value="punched_out">Punched Out</option>
                 </select>
               </div>

               {(filterStatus === 'punched_in' || filterStatus === 'punched_out') && (
                 <>
                   <div className="space-y-2 animate-in slide-in-from-left-2 duration-300">
                     <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Start Time</label>
                     <input 
                       type="time"
                       value={startTime}
                       onChange={(e) => setStartTime(e.target.value)}
                       className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-slate-200 focus:border-orange-500 outline-none transition-all cursor-pointer font-mono"
                     />
                   </div>
                   <div className="space-y-2 animate-in slide-in-from-left-2 duration-300">
                     <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">End Time</label>
                     <input 
                       type="time"
                       value={endTime}
                       onChange={(e) => setEndTime(e.target.value)}
                       className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-slate-200 focus:border-orange-500 outline-none transition-all cursor-pointer font-mono"
                     />
                   </div>
                 </>
               )}

               <div className="space-y-2">
                 <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Target Date</label>
                 <input 
                   type="date"
                   value={filterDate}
                   onChange={(e) => setFilterDate(e.target.value)}
                   className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-slate-200 focus:border-orange-500 outline-none transition-all cursor-pointer font-mono"
                 />
               </div>



               <div className="flex items-end">
                 <button 
                   onClick={fetchFilteredStudents}
                   disabled={loading}
                   className="w-full py-3 rounded-xl bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 font-bold border border-orange-500/20 transition-all flex items-center justify-center gap-2"
                 >
                   {loading ? 'Searching...' : 'Fetch Recipients'}
                   {!loading && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>}
                 </button>
               </div>
            </div>

            {filteredStudents.length > 0 && (
               <div className="space-y-6 animate-in slide-in-from-top-2 duration-300">
                 <div className="flex items-center justify-between px-1">
                   <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">
                     Target Students ({selectedEmails.length}/{filteredStudents.length})
                   </h3>
                   <button onClick={toggleSelectAll} className="text-xs font-semibold text-orange-400 hover:text-orange-300 transition-colors uppercase tracking-widest">
                      {selectedEmails.length === filteredStudents.length ? 'Deselect All' : 'Select All'}
                   </button>
                 </div>

                 <div className="flex items-center gap-2 px-1">
                    <input 
                      type="checkbox" 
                      id="hideSent" 
                      checked={hideSent} 
                      onChange={(e) => setHideSent(e.target.checked)}
                      className="accent-orange-500 cursor-pointer"
                    />
                    <label htmlFor="hideSent" className="text-[11px] font-medium text-slate-500 cursor-pointer select-none">
                      Hide already notified ({broadcastChannel.toUpperCase()})
                    </label>
                  </div>
                  <div className="max-h-60 overflow-y-auto custom-scrollbar border border-slate-800 rounded-2xl bg-slate-950/20 p-2 space-y-1">
                   {filteredStudents.filter(s => {
                      if (!hideSent) return true;
                      const logs = notifiedStudents.filter(n => n.recipientEmail === s.email && n.notificationType === notifType);
                      return !logs.map(n => n.channel).includes(broadcastChannel.toUpperCase());
                    }).map(student => {
                     const relevantLogs = notifiedStudents
                       .filter(n => n.recipientEmail === student.email && n.notificationType === notifType);
                     const channels = relevantLogs.map(n => n.channel);
                     const currentChannelName = broadcastChannel.toUpperCase();
                     const isNotifiedOnCurrent = channels.includes(currentChannelName);

                     return (
                       <div 
                         key={student.id} 
                         onClick={() => toggleStudent(student.email)}
                         className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border ${selectedEmails.includes(student.email) ? 'bg-orange-500/10 border-orange-500/30' : 'bg-transparent border-transparent hover:bg-slate-800/40'}`}
                       >
                         <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${selectedEmails.includes(student.email) ? 'bg-orange-500 border-orange-500' : 'border-slate-700'}`}>
                            {selectedEmails.includes(student.email) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                         </div>
                         <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-slate-200 truncate">{student.name}</p>
                              <div className="flex gap-1.5 ml-1">
                                {channels.includes('SMS') && <span className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_4px_rgba(249,115,22,0.6)]" title="SMS Sent" />}
                                {channels.includes('WHATSAPP') && <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_4px_rgba(16,185,129,0.6)]" title="WhatsApp Sent" />}
                                {channels.includes('EMAIL') && <span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_4px_rgba(59,130,246,0.6)]" title="Email Sent" />}
                              </div>
                            </div>
                          <div className="flex items-center gap-4">
                             <div className="flex flex-col">
                               <p className="text-[10px] text-slate-500 font-mono truncate">{student.email}</p>
                               {student.personalPhoneNumber && (
                                 <p className="text-[10px] text-orange-400/80 font-mono font-bold truncate">
                                   {student.personalPhoneNumber}
                                 </p>
                               )}
                             </div>

                             {(student.inTime || student.outTime) && (
                               <div className="flex items-center gap-3 border-l border-slate-800 pl-4 py-1">
                                 {student.inTime && (
                                   <div className="flex flex-col">
                                     <span className="text-[8px] uppercase text-slate-500 font-bold leading-none mb-1">In</span>
                                     <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">{student.inTime}</span>
                                   </div>
                                 )}
                                 {student.outTime && (
                                   <div className="flex flex-col">
                                     <span className="text-[8px] uppercase text-slate-500 font-bold leading-none mb-1">Out</span>
                                     <span className="text-[10px] font-mono text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded">{student.outTime}</span>
                                   </div>
                                 )}
                               </div>
                             )}
                          </div>
                         </div>
                         <div className="flex items-center gap-2">
                            <div className={`text-[9px] font-black px-2 py-1 rounded-md border uppercase tracking-wider ${
                              isNotifiedOnCurrent 
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                              : 'bg-slate-800/80 text-slate-500 border-slate-700'
                            }`}>
                               {isNotifiedOnCurrent ? 'Sent' : 'Yet to Send'}
                            </div>
                            <div className="text-[10px] font-bold text-slate-600 bg-slate-800/50 px-2 py-0.5 rounded uppercase">
                              {student.batchIdStr}
                            </div>
                         </div>
                       </div>
                     );
                   })}
                 </div>

                  <div className="space-y-4 pt-2">
                    {broadcastChannel === 'email' && (
                      <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Email Subject</label>
                        <input 
                          type="text"
                          placeholder="Enter email subject..."
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all font-medium"
                        />
                      </div>
                    )}
                    
                    {broadcastChannel === 'whatsapp' && (
                      <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Select WhatsApp Template</label>
                          <select 
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all font-medium"
                            value={selectedTemplate?.templateName || ''}
                            onChange={(e) => {
                              const t = waTemplates.find(tmpl => tmpl.templateName === e.target.value);
                              setSelectedTemplate(t || null);
                              setTemplateParams(t?.parameters ? t.parameters.split(',').map(() => '') : []);
                            }}
                          >
                            <option value="">Select a template...</option>
                            {waTemplates.map(t => (
                              <option key={t.id} value={t.templateName}>{t.templateName}</option>
                            ))}
                          </select>
                        </div>

                        {selectedTemplate ? (
                          <div className="p-4 rounded-xl bg-slate-950/40 border border-slate-800 space-y-3">
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Template Parameters</p>
                            <div className="grid grid-cols-1 gap-3">
                              {selectedTemplate.parameters?.split(',').map((p, idx) => {
                                const keyword = p.trim().toLowerCase();
                                const mappings = {
                                  'name': 'student_profiles.name',
                                  'email': 'student_profiles.email',
                                  'date': 'filter_date (UI)',
                                  'batch': 'student_profiles.batch_id_str',
                                  'time_in': 'student_attendance.time_in',
                                  'time_out': 'student_attendance.time_out'
                                };
                                const mapping = mappings[keyword];
                                const isAuto = !!mapping;

                                return (
                                  <div key={idx} className="space-y-1">
                                    <div className="flex items-center justify-between ml-1">
                                      <label className="text-[9px] font-bold text-slate-600 uppercase">
                                        {p.trim()}
                                      </label>
                                      {isAuto && (
                                        <span className="text-[8px] text-orange-500/70 font-mono bg-orange-500/5 px-1.5 py-0.5 rounded border border-orange-500/10 italic">
                                          auto-fill from {mapping}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {!isAuto && (
                                      <input 
                                        type="text"
                                        placeholder={`Enter ${p.trim()}...`}
                                        className="w-full bg-slate-900 border border-slate-800 rounded-lg p-2 text-xs text-slate-300 outline-none focus:border-orange-500 transition-all"
                                        value={templateParams[idx] || ''}
                                        onChange={(e) => {
                                          const newParams = [...templateParams];
                                          newParams[idx] = e.target.value;
                                          setTemplateParams(newParams);
                                        }}
                                      />
                                    )}
                                    {isAuto && (
                                      <div className="p-2 bg-slate-900/50 rounded-lg border border-slate-800/50 text-xs text-slate-500 italic flex items-center gap-2">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500/40"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                                        Dynamic value based on recipient
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-[10px] text-slate-500 italic leading-relaxed pt-2 border-t border-slate-800">
                              <span className="text-blue-400 font-bold mr-1">Preview:</span>
                              {selectedTemplate.bodyText}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                             <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                               Or Text Message
                             </label>
                             <textarea 
                               placeholder="Enter WhatsApp message..."
                               value={smsMessage}
                               onChange={(e) => setSmsMessage(e.target.value)}
                               className="w-full h-32 bg-slate-900/60 border border-slate-800 rounded-2xl p-4 text-slate-100 placeholder:text-slate-600 outline-none focus:border-orange-500 transition-all resize-none shadow-inner"
                             />
                          </div>
                        )}
                      </div>
                    )}

                    {broadcastChannel !== 'whatsapp' && (
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                          {broadcastChannel === 'sms' ? 'SMS Message Template' : 'Email Content (HTML Supported)'}
                        </label>
                        <textarea 
                          placeholder={broadcastChannel === 'sms' ? "Enter SMS message..." : "Write your email content..."}
                          value={smsMessage}
                          onChange={(e) => setSmsMessage(e.target.value)}
                          className="w-full h-32 bg-slate-900/60 border border-slate-800 rounded-2xl p-4 text-slate-100 placeholder:text-slate-600 outline-none focus:border-orange-500 transition-all resize-none shadow-inner"
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      {broadcastChannel === 'sms' ? (
                        <span className={`text-[10px] font-bold uppercase ${smsMessage.length > 160 ? 'text-rose-400' : 'text-slate-500'}`}>
                          {smsMessage.length} / 160 Characters
                        </span>
                      ) : (
                        <div />
                      )}
                      
                      <button 
                        onClick={broadcastChannel === 'sms' ? handleSendSms : broadcastChannel === 'email' ? handleSendEmail : handleSendWhatsapp}
                        disabled={sending || selectedEmails.length === 0 || 
                                 (broadcastChannel === 'sms' && !smsMessage.trim()) || 
                                 (broadcastChannel === 'email' && (!smsMessage.trim() || !emailSubject.trim())) ||
                                 (broadcastChannel === 'whatsapp' && !smsMessage.trim() && !selectedTemplate)}
                        className={`px-8 py-3 rounded-xl font-bold transition-all flex items-center gap-2 shadow-lg ${
                          (!sending && selectedEmails.length > 0 && 
                           ((broadcastChannel === 'sms' && smsMessage.trim()) || 
                            (broadcastChannel === 'email' && smsMessage.trim() && emailSubject.trim()) ||
                            (broadcastChannel === 'whatsapp' && (smsMessage.trim() || selectedTemplate))))
                          ? 'bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white shadow-orange-500/20 animate-in zoom-in-95'
                          : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                        }`}
                      >
                        {sending ? 'Broadcasting...' : `Broadcast ${broadcastChannel === 'sms' ? 'SMS' : broadcastChannel === 'email' ? 'Email' : 'WhatsApp'}`}
                        {!sending && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"></path><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>}
                      </button>
                    </div>
                  </div>
               </div>
            )}

            {hasSearched && filteredStudents.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-12 px-4 bg-slate-950/20 border border-slate-800/50 rounded-2xl animate-in fade-in duration-500">
                  <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center text-slate-500 mb-4">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-300">No Recipients Found</h3>
                  <p className="text-sm text-slate-500 text-center max-w-xs mt-1">
                    We couldn't find any students matching "{filterStatus}" for {filterDate}. Try adjusting your filters.
                  </p>
                </div>
             )}
          </section>

          {/* Delivery History */}
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <h2 className="text-xl font-semibold text-slate-200 mb-6">Recent Broadcasts</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                    <th className="pb-3 font-semibold">Message Subject</th>
                    <th className="pb-3 font-semibold">Channel</th>
                    <th className="pb-3 font-semibold">Audience</th>
                    <th className="pb-3 font-semibold">Status / Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {NOTIFICATION_HISTORY.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-4 text-sm font-medium text-slate-200">{log.title}</td>
                      <td className="py-4">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded border ${
                          log.channel.includes('WhatsApp') ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                          log.channel.includes('SMS') ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                          'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                        }`}>
                          {log.channel}
                        </span>
                      </td>
                      <td className="py-4 text-sm text-slate-400">{log.sentTo}</td>
                      <td className="py-4 text-sm">
                        <span className="block text-slate-300 font-medium">{log.status}</span>
                        <span className="text-xs text-slate-500">{log.time}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

        </div>

        {/* Right Column: Channels Status */}
        <div className="space-y-6">
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <h2 className="text-lg font-semibold text-slate-200 mb-6">System Health</h2>
            
            <div className="space-y-4">
              
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/30 border border-slate-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200">Push App</h4>
                    <p className="text-[10px] text-slate-500">Connected</p>
                  </div>
                </div>
                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse"></div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/30 border border-slate-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200">WhatsApp API</h4>
                    <p className="text-[10px] text-slate-500">Authenticated (Meta)</p>
                  </div>
                </div>
                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse"></div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/30 border border-slate-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200">Email (SMTP)</h4>
                    <p className="text-[10px] text-slate-500">SendGrid Active</p>
                  </div>
                </div>
                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse"></div>
              </div>

            </div>
          </section>
        </div>

      </div>

      {/* SMTP Settings Modal */}
      {showSmtpModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-3xl overflow-y-auto max-h-[90vh] shadow-2xl animate-in zoom-in-95 duration-300 custom-scrollbar">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-orange-500/10 text-orange-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-200">Email SMTP Settings</h3>
                  <p className="text-xs text-slate-500">Configure your outgoing mail server for white-labeled broadcasts.</p>
                </div>
              </div>
              <button 
                onClick={() => setShowSmtpModal(false)}
                className="p-2 hover:bg-slate-800 rounded-full text-slate-500 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">SMTP Host</label>
                  <input 
                    type="text"
                    placeholder="smtp.example.com"
                    value={smtpSettings.host}
                    onChange={(e) => setSmtpSettings({...smtpSettings, host: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Port</label>
                  <input 
                    type="number"
                    placeholder="587"
                    value={smtpSettings.port}
                    onChange={(e) => setSmtpSettings({...smtpSettings, port: parseInt(e.target.value)})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Username / Key</label>
                  <input 
                    type="text"
                    placeholder="User ID or API Key"
                    value={smtpSettings.username}
                    onChange={(e) => setSmtpSettings({...smtpSettings, username: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Password / Secret</label>
                  <input 
                    type="password"
                    placeholder="••••••••••••"
                    value={smtpSettings.password}
                    onChange={(e) => setSmtpSettings({...smtpSettings, password: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">From Email Address</label>
                <input 
                  type="email"
                  placeholder="notifications@yourdomain.com"
                  value={smtpSettings.fromEmail}
                  onChange={(e) => setSmtpSettings({...smtpSettings, fromEmail: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                />
              </div>

              <div className="flex gap-6 pt-2">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div 
                    onClick={() => setSmtpSettings({...smtpSettings, auth: !smtpSettings.auth})}
                    className={`w-10 h-5 rounded-full relative transition-all ${smtpSettings.auth ? 'bg-orange-500' : 'bg-slate-700'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${smtpSettings.auth ? 'right-0.5' : 'left-0.5'}`} />
                  </div>
                  <span className="text-sm font-semibold text-slate-400 group-hover:text-slate-200">SMTP Auth</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer group">
                  <div 
                    onClick={() => setSmtpSettings({...smtpSettings, startTls: !smtpSettings.startTls})}
                    className={`w-10 h-5 rounded-full relative transition-all ${smtpSettings.startTls ? 'bg-orange-500' : 'bg-slate-700'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${smtpSettings.startTls ? 'right-0.5' : 'left-0.5'}`} />
                  </div>
                  <span className="text-sm font-semibold text-slate-400 group-hover:text-slate-200">StartTLS</span>
                </label>
              </div>
            </div>

            <div className="p-6 bg-slate-800/30 flex items-center justify-end gap-3">
              <button 
                onClick={() => setShowSmtpModal(false)}
                className="px-6 py-2 rounded-xl text-sm font-bold text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveSmtp}
                className="px-8 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 text-white font-bold shadow-lg shadow-orange-500/20 active:scale-95 transition-all"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Settings Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-3xl overflow-y-auto max-h-[90vh] shadow-2xl animate-in zoom-in-95 duration-300 custom-scrollbar">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-orange-500/10 text-orange-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-200">WhatsApp API Settings</h3>
                  <p className="text-xs text-slate-500">Configure your Meta Cloud API for automated student messaging.</p>
                </div>
              </div>
              <button 
                onClick={() => setShowWhatsAppModal(false)}
                className="p-2 hover:bg-slate-800 rounded-full text-slate-500 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Meta API Key (Permanent Token)</label>
                <input 
                  type="password"
                  placeholder="EAABw..."
                  value={whatsappSettings.apiKey}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, apiKey: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Phone Number ID</label>
                  <input 
                    type="text"
                    placeholder="1234567890..."
                    value={whatsappSettings.phoneNumberId}
                    onChange={(e) => setWhatsappSettings({...whatsappSettings, phoneNumberId: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Business Account ID</label>
                  <input 
                    type="text"
                    placeholder="123456... "
                    value={whatsappSettings.businessAccountId}
                    onChange={(e) => setWhatsappSettings({...whatsappSettings, businessAccountId: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-800 space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-950 rounded-2xl border border-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">Automated Attendance Notifications</p>
                        <p className="text-[10px] text-slate-500">Auto-send WhatsApp alerts to parents on student entry/exit.</p>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={() => setWhatsappSettings({...whatsappSettings, whatsappAutoNotifyEnabled: !whatsappSettings.whatsappAutoNotifyEnabled})}
                      className={`w-12 h-6 rounded-full relative transition-colors ${whatsappSettings.whatsappAutoNotifyEnabled ? 'bg-emerald-500' : 'bg-slate-800'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${whatsappSettings.whatsappAutoNotifyEnabled ? 'right-1' : 'left-1'}`} />
                    </button>
                  </div>

                  {whatsappSettings.whatsappAutoNotifyEnabled && (
                    <div className="p-5 bg-slate-950/50 rounded-2xl border border-slate-800 space-y-4 animate-in slide-in-from-top-2">
                       <div className="flex items-center gap-2 text-emerald-400">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                        <span className="text-[10px] font-bold uppercase tracking-widest">Real-time Delivery Active</span>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Time-In Template</label>
                        <select 
                          value={whatsappSettings.whatsappTimeInTemplate}
                          onChange={(e) => setWhatsappSettings({...whatsappSettings, whatsappTimeInTemplate: e.target.value})}
                          className="w-full bg-slate-900 border border-slate-800 rounded-xl p-2.5 text-sm text-slate-200 outline-none focus:border-emerald-500"
                        >
                          <option value="">Select Template...</option>
                          {waTemplates.map(t => (
                            <option key={t.id} value={t.templateName}>{t.templateName}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Time-Out Template</label>
                        <select 
                          value={whatsappSettings.whatsappTimeOutTemplate}
                          onChange={(e) => setWhatsappSettings({...whatsappSettings, whatsappTimeOutTemplate: e.target.value})}
                          className="w-full bg-slate-900 border border-slate-800 rounded-xl p-2.5 text-sm text-slate-200 outline-none focus:border-emerald-500"
                        >
                          <option value="">Select Template...</option>
                          {waTemplates.map(t => (
                            <option key={t.id} value={t.templateName}>{t.templateName}</option>
                          ))}
                        </select>
                      </div>
                      <p className="text-[10px] text-slate-500 italic">
                        Note: Templates should follow the parameter order: [1: Name], [2: Date], [3: Time].
                      </p>
                    </div>
                  )}
                </div>
            </div>

            <div className="p-6 bg-slate-800/30 flex items-center justify-end gap-3">
              <button 
                onClick={() => setShowWhatsAppModal(false)}
                className="px-6 py-2 rounded-xl text-sm font-bold text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveWhatsAppSettings}
                className="px-8 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-emerald-500 text-white font-bold shadow-lg shadow-emerald-500/20 active:scale-95 transition-all"
              >
                Save WA Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS Settings Modal */}
      {showSmsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-3xl overflow-y-auto max-h-[90vh] shadow-2xl animate-in zoom-in-95 duration-300 custom-scrollbar">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-orange-500/10 text-orange-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-200">SMS Gateway Settings</h3>
                  <p className="text-xs text-slate-500">Configure your SMS provider for automated student alerts.</p>
                </div>
              </div>
              <button 
                onClick={() => setShowSmsModal(false)}
                className="p-2 hover:bg-slate-800 rounded-full text-slate-500 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">SMS Provider</label>
                <select 
                  value={smsSettings.provider}
                  onChange={(e) => setSmsSettings({...smsSettings, provider: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm cursor-pointer"
                >
                  <option value="msg91">Msg91 (Flow API)</option>
                  <option value="twilio">Twilio (SMS API)</option>
                  <option value="fast2sms">Fast2SMS (Quick SMS)</option>
                </select>
              </div>

              {smsSettings.provider === 'twilio' && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Twilio Account SID</label>
                  <input 
                    type="text"
                    placeholder="AC..."
                    value={smsSettings.accountSid}
                    onChange={(e) => setSmsSettings({...smsSettings, accountSid: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm font-mono"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                  {smsSettings.provider === 'twilio' ? 'Twilio Auth Token' : 
                   smsSettings.provider === 'fast2sms' ? 'Fast2SMS API Key' : 'SMS API Key'}
                </label>
                <input 
                  type="password"
                  placeholder={smsSettings.provider === 'twilio' ? "Enter Auth Token" : 
                               smsSettings.provider === 'fast2sms' ? "Enter Fast2SMS API Key" : "Enter API Key"}
                  value={smsSettings.apiKey}
                  onChange={(e) => setSmsSettings({...smsSettings, apiKey: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm font-mono"
                />
              </div>

              {smsSettings.provider !== 'fast2sms' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">
                    {smsSettings.provider === 'twilio' ? 'Twilio From Number' : 'Sender ID / Template ID'}
                  </label>
                  <input 
                    type="text"
                    placeholder={smsSettings.provider === 'twilio' ? "+1234567890" : "e.g. PENSNS"}
                    value={smsSettings.senderId}
                    onChange={(e) => setSmsSettings({...smsSettings, senderId: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 outline-none focus:border-orange-500 transition-all text-sm"
                  />
                </div>
              )}
            </div>

            <div className="p-6 bg-slate-800/30 flex items-center justify-end gap-3">
              <button 
                onClick={() => setShowSmsModal(false)}
                className="px-6 py-2 rounded-xl text-sm font-bold text-slate-400 hover:text-white transition-colors"
                >
                Cancel
              </button>
              <button 
                onClick={handleSaveSmsSettings}
                className="px-8 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 text-white font-bold shadow-lg shadow-orange-500/20 active:scale-95 transition-all"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
